import {
  EventSchedule,
  Calendar,
  Catalog,
  Document as DocumentIcon,
  Time,
} from '@carbon/react/icons';
import { Date } from 'components/date';
import type { DocumentRow, CardInterpretation } from '../types';
import { NOT_AVAILABLE } from './utils';
import styles from './document-card.module.scss';

interface DocumentCardDatesProps {
  document: DocumentRow;
  primaryInterp?: CardInterpretation;
}

/**
 * The card's dates column: each present date with its icon, or "Not Available".
 * @param {DocumentCardDatesProps} props The column.
 * @return {JSX.Element} The column.
 */
export function DocumentCardDates({
  document,
  primaryInterp,
}: DocumentCardDatesProps) {
  const dateRows = [
    {
      key: 'transaction',
      label: 'Transaction Date',
      value: primaryInterp?.interpretationDate,
      note: primaryInterp?.interpretationDateSource,
      Icon: EventSchedule,
    },
    {
      key: 'effective',
      label: 'Effective Date',
      value: document.effectiveDate,
      Icon: Calendar,
    },
    {
      key: 'instrument',
      label: 'Instrument Date',
      value: document.instrumentDate,
      Icon: Catalog,
    },
    {
      key: 'file',
      label: 'File Date',
      value: document.fileDate,
      Icon: DocumentIcon,
    },
    {
      key: 'recorded',
      label: 'Recorded Date',
      value: document.recordedDate,
      Icon: Time,
    },
  ].flatMap((row) => (row.value ? [{ ...row, value: row.value }] : []));

  if (dateRows.length === 0) {
    return (
      <div className={styles.dates} role="group" aria-label="Dates">
        <span className={styles.muted}>{NOT_AVAILABLE}</span>
      </div>
    );
  }
  return (
    <dl className={styles.dates}>
      {dateRows.map(({ key, label, value, note, Icon }) => (
        <div key={key} className={styles.dateRow}>
          <dt>
            <Icon className={styles.dateIcon} />
            {label}
          </dt>
          <dd>
            <Date date={value} />
            {note && <span className={styles.dateNote}>({note})</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}
